import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Gauge, Pencil, Trash2, UserMinus, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Can } from '../../app/guards';
import { Badge, Dialog, Field, PageHeader, Skeleton, Spinner, Table, Td, Th, useConfirm, useToast } from '../../components/ui';
import { api, type Paginated } from '../../lib/api';
import { fmtAgo, fmtDate, fmtDateTime, fmtNumber } from '../../lib/format';
import { SourceBadge, VehicleDialog, VehicleThumb, vehicleStatusTone, type Group, type VehicleRow } from './VehiclesPage';

interface VehicleDetail extends VehicleRow {
  vin: string | null;
  color: string | null;
  fuelType: string | null;
  tankCapacityL: string | null;
  speedLimitKph: number | null;
  odometerSource: string | null;
  odometerUpdatedAt: string | null;
  notes: string | null;
  createdAt: string;
  documents: { id: string; type: string; title: string; expiresAt: string | null }[];
  schedules: { id: string; title: string; nextDueAt: string | null; nextDueOdometerKm: string | null }[];
  assignments: { id: string; startedAt: string; driver: { id: string; fullName: string; phone: string } }[];
  liveState: (VehicleRow['liveState'] & { lat: number | null; lon: number | null; address: string | null; ignition: boolean | null; fuelLevelPct: number | null }) | null;
}
interface DriverLite { id: string; fullName: string; phone: string; status: string }
interface Assignment { id: string; kind: string; startedAt: string; endedAt: string | null; driver: { id: string; fullName: string } }

export function VehicleDetailPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [odo, setOdo] = useState<string | null>(null);

  const q = useQuery({ queryKey: ['vehicle', id], queryFn: () => api<VehicleDetail>(`/vehicles/${id}`) });
  const groups = useQuery({ queryKey: ['vehicle-groups'], queryFn: () => api<Group[]>('/vehicle-groups') });
  const history = useQuery({ queryKey: ['vehicle-assignments', id], queryFn: () => api<Assignment[]>(`/vehicles/${id}/assignments`) });
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['vehicle', id] }); void qc.invalidateQueries({ queryKey: ['vehicles'] }); void qc.invalidateQueries({ queryKey: ['vehicle-assignments', id] }); };

  const unassign = useMutation({ mutationFn: () => api(`/vehicles/${id}/unassign`, { method: 'POST' }), onSuccess: () => { toast.success(t('common.saved')); invalidate(); }, onError: toast.error });
  const remove = useMutation({ mutationFn: () => api(`/vehicles/${id}`, { method: 'DELETE' }), onSuccess: () => { toast.success(t('common.saved')); nav('/vehicles'); }, onError: toast.error });
  const correct = useMutation({ mutationFn: (km: number) => api(`/vehicles/${id}/odometer`, { method: 'POST', json: { odometerKm: km } }), onSuccess: () => { toast.success(t('common.saved')); setOdo(null); invalidate(); }, onError: toast.error });

  if (q.isLoading || !q.data) return <Skeleton className="h-96" />;
  const v = q.data;
  const current = v.assignments[0]?.driver ?? null;

  return (
    <div className="flex flex-col gap-5">
      <Link to="/vehicles" className="flex items-center gap-1 text-sm text-text-muted hover:text-text"><ArrowRight size={14} className="ltr:rotate-180" /> {t('nav.vehicles')}</Link>
      <PageHeader
        title={<span className="flex items-center gap-3"><VehicleThumb plate={v.plateAr} photoUrl={v.photoUrl} size="lg" />{v.plateAr}</span>}
        subtitle={[v.plateLatin, v.make, v.model, v.year].filter(Boolean).join(' · ')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Badge tone={vehicleStatusTone(v.status)} dot>{t(`vehicles.statuses.${v.status}`)}</Badge>
            <Can perm="vehicles:update"><button className="btn-secondary" onClick={() => setEditing(true)}><Pencil size={16} /> {t('common.edit')}</button></Can>
            <Can perm="vehicles:delete"><button className="btn-ghost text-danger-fg" onClick={() => confirmDialog(t('vehicles.deleteConfirm', { plate: v.plateAr }), { danger: true }).then((ok) => ok && remove.mutate())}><Trash2 size={16} /> {t('common.delete')}</button></Can>
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={t('vehicles.odometer')} action={<Can perm="vehicles:update"><button className="btn-ghost !min-h-8 !px-2" onClick={() => setOdo(String(Math.round(Number(v.odometerKm))))} aria-label={t('vehicles.correctOdometer')}><Gauge size={14} /></button></Can>}>
          {fmtNumber(v.odometerKm, i18n.language)} <span className="text-sm text-text-faint">{t('units.km')}</span>
          <div className="text-xs font-normal text-text-faint">{v.odometerSource ? t(`vehicles.odoSources.${v.odometerSource}`) : ''} · {fmtAgo(v.odometerUpdatedAt, i18n.language)}</div>
        </Stat>
        <Stat label={t('vehicles.driver')}>
          {current ? <Link to={`/drivers/${current.id}`} className="hover:underline">{current.fullName}</Link> : <span className="text-text-faint">{t('vehicles.noDriver')}</span>}
          <div className="mt-1 flex gap-1">
            <Can perm="vehicles:assign">
              <button className="btn-ghost !min-h-8 !px-2 text-xs" onClick={() => setAssigning(true)}><UserPlus size={14} /> {t('vehicles.assign')}</button>
              {current ? <button className="btn-ghost !min-h-8 !px-2 text-xs text-danger-fg" onClick={() => unassign.mutate()}><UserMinus size={14} /> {t('vehicles.unassign')}</button> : null}
            </Can>
          </div>
        </Stat>
        <Stat label={t('vehicles.source')}>
          <span className="flex items-center gap-2">{t(`vehicles.sources.${v.trackingSource}`)} <SourceBadge source={v.trackingSource} /></span>
          <div className="text-xs font-normal text-text-faint" dir="ltr">{v.device ? `IMEI ${v.device.imei}` : t('vehicles.noDevice')}</div>
        </Stat>
        <Stat label={t('vehicles.liveStatus')}>
          {v.liveState ? <>{t(`status.${v.liveState.status}`)}<div className="text-xs font-normal text-text-faint">{fmtAgo(v.liveState.lastAt, i18n.language)}{v.liveState.speedKph != null ? ` · ${Math.round(v.liveState.speedKph)} ${t('units.kph')}` : ''}</div></> : <span className="text-text-faint">{t('status.UNKNOWN')}</span>}
        </Stat>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="card p-5">
          <h3 className="mb-3 text-sm font-bold">{t('vehicles.details')}</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <Row k={t('vehicles.type')} v={t(`vehicles.types.${v.type}`)} />
            <Row k={t('vehicles.fuelType')} v={v.fuelType ? t(`vehicles.fuelTypes.${v.fuelType}`) : '—'} />
            <Row k={t('vehicles.tankCapacity')} v={v.tankCapacityL ? `${fmtNumber(v.tankCapacityL, i18n.language)} ${t('units.liter')}` : '—'} />
            <Row k={t('vehicles.speedLimit')} v={v.speedLimitKph ? `${v.speedLimitKph} ${t('units.kph')}` : t('vehicles.speedLimitDefault')} />
            <Row k={t('vehicles.group')} v={v.group?.name ?? '—'} />
            <Row k={t('vehicles.vin')} v={<span dir="ltr">{v.vin ?? '—'}</span>} />
            <Row k={t('vehicles.color')} v={v.color ?? '—'} />
            <Row k={t('vehicles.createdAt')} v={fmtDate(v.createdAt, i18n.language)} />
          </dl>
          {v.notes ? <p className="mt-3 rounded-md bg-surface-2 p-3 text-sm text-text-2">{v.notes}</p> : null}
        </section>

        <section className="card p-5">
          <h3 className="mb-3 text-sm font-bold">{t('vehicles.assignmentHistory')}</h3>
          {!history.data?.length ? <p className="text-sm text-text-muted">{t('common.empty')}</p> : (
            <ul className="divide-y divide-border-soft text-sm">
              {history.data.map((a) => (
                <li key={a.id} className="flex items-center justify-between py-2">
                  <Link to={`/drivers/${a.driver.id}`} className="font-medium hover:underline">{a.driver.fullName}</Link>
                  <span className="text-xs text-text-muted">{fmtDateTime(a.startedAt, i18n.language)} → {a.endedAt ? fmtDateTime(a.endedAt, i18n.language) : <Badge tone="success">{t('vehicles.current')}</Badge>}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card p-5">
          <div className="mb-3 flex items-center justify-between"><h3 className="text-sm font-bold">{t('vehicles.maintenanceSchedules')}</h3><Link to={`/maintenance?view=schedules&vehicleId=${id}`} className="text-xs font-semibold text-brand-ink hover:underline">{t('common.viewAll')}</Link></div>
          {!v.schedules.length ? <p className="text-sm text-text-muted">{t('vehicles.noSchedules')}</p> : (
            <Table className="!rounded-lg"><thead><tr><Th>{t('vehicles.item')}</Th><Th>{t('vehicles.nextDue')}</Th></tr></thead><tbody>{v.schedules.map((s) => <tr key={s.id}><Td>{s.title}</Td><Td>{s.nextDueOdometerKm ? `${fmtNumber(s.nextDueOdometerKm, i18n.language)} ${t('units.km')}` : fmtDate(s.nextDueAt, i18n.language)}</Td></tr>)}</tbody></Table>
          )}
        </section>

        <section className="card p-5">
          <div className="mb-3 flex items-center justify-between"><h3 className="text-sm font-bold">{t('vehicles.documents')}</h3><Link to={`/maintenance?view=documents&vehicleId=${id}`} className="text-xs font-semibold text-brand-ink hover:underline">{t('common.viewAll')}</Link></div>
          {!v.documents.length ? <p className="text-sm text-text-muted">{t('vehicles.noDocuments')}</p> : (
            <ul className="divide-y divide-border-soft text-sm">{v.documents.map((d) => <li key={d.id} className="flex justify-between py-2"><span>{d.title}</span><span className="text-xs text-text-muted">{fmtDate(d.expiresAt, i18n.language)}</span></li>)}</ul>
          )}
        </section>
      </div>

      {editing ? <VehicleDialog vehicle={v} groups={groups.data ?? []} onClose={() => setEditing(false)} /> : null}
      {assigning ? <AssignDialog vehicleId={id} onClose={() => setAssigning(false)} onDone={invalidate} /> : null}
      {odo !== null ? (
        <Dialog open onClose={() => setOdo(null)} title={t('vehicles.correctOdometer')} footer={<><button className="btn-secondary" onClick={() => setOdo(null)}>{t('common.cancel')}</button><button className="btn-primary" disabled={correct.isPending || odo === ''} onClick={() => correct.mutate(Number(odo))}>{correct.isPending ? <Spinner /> : null}{t('common.save')}</button></>}>
          <Field label={t('vehicles.odometer')} hint={t('vehicles.correctOdometerHint')}>{(fid) => <input id={fid} type="number" dir="ltr" className="input" value={odo} onChange={(e) => setOdo(e.target.value)} />}</Field>
        </Dialog>
      ) : null}
    </div>
  );
}

function Stat({ label, children, action }: { label: string; children: React.ReactNode; action?: React.ReactNode }) {
  return <div className="card p-4"><div className="flex items-center justify-between text-xs text-text-muted">{label}{action}</div><div className="mt-1 text-lg font-bold">{children}</div></div>;
}
function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return <><dt className="text-text-muted">{k}</dt><dd className="font-medium">{v}</dd></>;
}

function AssignDialog({ vehicleId, onClose, onDone }: { vehicleId: string; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [driverId, setDriverId] = useState('');
  const drivers = useQuery({ queryKey: ['drivers-lite', search], queryFn: () => api<Paginated<DriverLite>>(`/drivers?status=ACTIVE&pageSize=50&q=${encodeURIComponent(search)}`) });
  const assign = useMutation({ mutationFn: () => api(`/vehicles/${vehicleId}/assign`, { method: 'POST', json: { driverId } }), onSuccess: () => { toast.success(t('common.saved')); onDone(); onClose(); }, onError: toast.error });
  return (
    <Dialog open onClose={onClose} title={t('vehicles.assign')} footer={<><button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button><button className="btn-primary" disabled={!driverId || assign.isPending} onClick={() => assign.mutate()}>{assign.isPending ? <Spinner /> : null}{t('common.save')}</button></>}>
      <div className="grid gap-3">
        <input className="input" placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="max-h-72 overflow-y-auto rounded-md border border-border">
          {drivers.data?.data.map((d) => (
            <label key={d.id} className="flex cursor-pointer items-center gap-3 border-b border-border-soft px-3 py-2.5 last:border-0 hover:bg-surface-hover has-[:checked]:bg-brand-soft">
              <input type="radio" name="driver" className="accent-brand" checked={driverId === d.id} onChange={() => setDriverId(d.id)} />
              <span className="flex-1 text-sm font-medium">{d.fullName}</span>
              <span className="text-xs text-text-muted" dir="ltr">{d.phone}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-text-faint">{t('vehicles.assignHint')}</p>
      </div>
    </Dialog>
  );
}
